/**
 * useOrgChartStartPicker — Start-picker state + handlers for a freshly
 * created org chart (AI prompt / manual starter).
 * Mirrors `useFlowchartStartPicker`; seeds run through the Seed Registry
 * (`runSeedById`) so the picker and deep links share one implementation.
 */

import { useCallback, useState } from 'react';
import type * as Y from 'yjs';
import type { CommandManager } from '../../../core/commands/CommandManager';
import { runSeedById } from '../../../core/seeds/runSeed';
import { generateOrgChartPromptSeed } from '../seeds/orgPromptSeed';

type RequestFn = (input: string, init?: RequestInit) => Promise<Response>;

export interface UseOrgChartStartPickerOptions {
  commandManager: CommandManager;
  doc?: Y.Doc | null;
  requestFn?: RequestFn;
  /** Folder the diagram lives in — enables the source-backed starter. */
  folderId?: string | null;
  /** Fires after a starter seeds content, so the shell can zoom-to-fit. */
  onSeeded: () => void;
}

export interface OrgChartStartPickerState {
  /** True once the user has picked any starter — hides the picker overlay. */
  chosen: boolean;
  generatingPrompt: boolean;
  onGenerateFromPrompt: (prompt: string) => Promise<boolean>;
  onSelectStarter: (seedId: string) => Promise<void>;
}

export function useOrgChartStartPicker({
  commandManager,
  doc,
  requestFn,
  folderId,
  onSeeded,
}: UseOrgChartStartPickerOptions): OrgChartStartPickerState {
  const [chosen, setChosen] = useState(false);
  const [generatingPrompt, setGeneratingPrompt] = useState(false);

  const onGenerateFromPrompt = useCallback(
    async (prompt: string): Promise<boolean> => {
      const trimmed = prompt.trim();
      if (!trimmed || !requestFn) return false;
      setGeneratingPrompt(true);
      try {
        const seeded = await generateOrgChartPromptSeed(commandManager, trimmed, requestFn, doc);
        if (seeded) {
          setChosen(true);
          onSeeded();
        }
        return seeded;
      } catch (error) {
        console.warn('[orgChartStarter] failed to generate from prompt', error);
        return false;
      } finally {
        setGeneratingPrompt(false);
      }
    },
    [commandManager, doc, onSeeded, requestFn],
  );

  const onSelectStarter = useCallback(
    async (seedId: string): Promise<void> => {
      setChosen(true);
      try {
        const seeded = await runSeedById(commandManager, seedId, { doc, folderId, requestFn });
        if (seeded) {
          onSeeded();
        }
      } catch (error) {
        console.warn('[orgChartStarter] failed to seed', seedId, error);
      }
    },
    [commandManager, doc, folderId, onSeeded, requestFn],
  );

  return { chosen, generatingPrompt, onGenerateFromPrompt, onSelectStarter };
}
